import React, { useEffect, useState } from "react";
import { mondayService } from "../../services/monday.service";
import { analyticsService } from "../../services/analytics.service";

interface AccountBreakdown {
  name: string;
  count: number;
}

interface KpiItem {
  label: string;
  goal: number;
  current?: number;
  accounts?: AccountBreakdown[];
}

interface KpiBoxProps {
  title: string;
  total: number;
  items: KpiItem[];
  accentColor: string;
  colors: string[];
  notice?: string;
  loading?: boolean;
}

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const SkeletonBar: React.FC<{ width: number | string; height: number }> = ({
  width,
  height,
}) => (
  <>
    <style>{shimmer}</style>
    <div
      style={{
        width,
        height,
        borderRadius: height / 2,
        background:
          "linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.4s infinite linear",
      }}
    />
  </>
);

const KpiBox: React.FC<KpiBoxProps> = ({
  title,
  total,
  items,
  accentColor,
  colors,
  notice,
  loading,
}) => {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);
  const totalCurrent = items.reduce(
    (sum, item) => sum + (item.current ?? 0),
    0
  );
  const overallPct = Math.round((totalCurrent / total) * 100);

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#fff",
        borderRadius: 10,
        padding: "28px 32px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: `2px solid ${accentColor}`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: "#1a1a2e",
            }}
          >
            {title}
          </h2>
          <span style={{ fontSize: 13, color: "#aaa", fontWeight: 400 }}>
            Q2 2026
          </span>
          {notice && (
            <span
              style={{
                fontSize: 11,
                color: "#bbb",
                fontStyle: "italic",
                marginLeft: 4,
              }}
            >
              {notice}
            </span>
          )}
        </div>
        <div
          style={{
            backgroundColor: accentColor + "18",
            color: accentColor,
            fontSize: 32,
            fontWeight: 700,
            borderRadius: 20,
            padding: "8px 24px",
            letterSpacing: 0.3,
            whiteSpace: "nowrap",
          }}
        >
          Target: {total.toLocaleString()}
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SkeletonBar width='100%' height={10} />
          <div style={{ height: 1, backgroundColor: "#f0f0f0" }} />
          {items.map((item) => (
            <div
              key={item.label}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <SkeletonBar width={120} height={12} />
                <SkeletonBar width={80} height={12} />
              </div>
              <SkeletonBar width='100%' height={8} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Overall progress */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                Overall Progress
              </span>
              <span
                style={{ fontSize: 16, fontWeight: 600, color: accentColor }}
              >
                {totalCurrent.toLocaleString()} / {total.toLocaleString()}{" "}
                &mdash; {overallPct}%
              </span>
            </div>
            <div
              style={{
                height: 10,
                backgroundColor: "#ececec",
                borderRadius: 5,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${overallPct}%`,
                  backgroundColor: accentColor,
                  borderRadius: 5,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>

          {/* Divider */}
          <div
            style={{ height: 1, backgroundColor: "#f0f0f0", marginBottom: 20 }}
          />

          {/* Per-item rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {items.map((item, i) => {
              const current = item.current ?? 0;
              const pct = Math.round((current / item.goal) * 100);
              const color = colors[i % colors.length];
              const isExpanded = expandedIndex === i;
              const hasAccounts = item.accounts && item.accounts.length > 0;
              return (
                <div key={item.label}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 7,
                      cursor: hasAccounts ? "pointer" : "default",
                    }}
                    onClick={() => hasAccounts && setExpandedIndex(isExpanded ? null : i)}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{ fontSize: 17, fontWeight: 600, color: "#333" }}
                      >
                        {item.label}
                      </span>
                      {hasAccounts && (
                        <span style={{ fontSize: 13, color: "#aaa", userSelect: "none" }}>
                          {isExpanded ? "▲" : "▼"} {item.accounts!.length} accounts
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 16, color: "#777" }}>
                      <strong style={{ color: "#333" }}>
                        {current.toLocaleString()}
                      </strong>
                      {" / "}
                      {item.goal.toLocaleString()}
                      <span style={{ marginLeft: 6, color, fontWeight: 700 }}>
                        {pct}%
                      </span>
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      backgroundColor: color + "22",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${pct}%`,
                        backgroundColor: color,
                        borderRadius: 4,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                  {isExpanded && hasAccounts && (
                    <div
                      style={{
                        marginTop: 10,
                        backgroundColor: "#fafafa",
                        border: "1px solid #ececec",
                        borderRadius: 6,
                        padding: "8px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                      }}
                    >
                      {item.accounts!
                        .slice()
                        .sort((a, b) => b.count - a.count)
                        .map((acc) => (
                          <div
                            key={acc.name}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 12,
                              color: "#555",
                            }}
                          >
                            <span>{acc.name}</span>
                            <span style={{ fontWeight: 600, color: "#333" }}>
                              {acc.count.toLocaleString()}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

interface Q2KPIProps {
  database?: string;
}

export const Q2KPI: React.FC<Q2KPIProps> = ({ database }) => {
  const [eligibleLoading, setEligibleLoading] = useState(true);
  const [sagiCount, setSagiCount] = useState<number | undefined>(undefined);
  const [arielCount, setArielCount] = useState<number | undefined>(undefined);
  const [sagiItems, setSagiItems] = useState<AccountBreakdown[]>([]);
  const [arielItems, setArielItems] = useState<AccountBreakdown[]>([]);
  const [mondayConfigured, setMondayConfigured] = useState(true);

  const [activeUsersLoading, setActiveUsersLoading] = useState(true);
  const [peakMau, setPeakMau] = useState<number | undefined>(undefined);
  const [peakMauMonth, setPeakMauMonth] = useState<string>('');
  const [currentEmployerMAU, setCurrentEmployerMAU] = useState<number | undefined>(undefined);
  const [newEmployerMAU, setNewEmployerMAU] = useState<number | undefined>(undefined);
  const [currentBreakdown, setCurrentBreakdown] = useState<AccountBreakdown[]>([]);
  const [newBreakdown, setNewBreakdown] = useState<AccountBreakdown[]>([]);

  useEffect(() => {
    mondayService
      .getEligibleEmployees()
      .then((data) => {
        setMondayConfigured(data.configured);
        if (data.configured) {
          setSagiCount(data.sagi);
          setArielCount(data.ariel);
          setSagiItems(data.sagiItems ?? []);
          setArielItems(data.arielItems ?? []);
        }
      })
      .catch(() => {
        setMondayConfigured(false);
      })
      .finally(() => {
        setEligibleLoading(false);
      });
  }, []);

  useEffect(() => {
    setActiveUsersLoading(true);
    analyticsService
      .getActiveUsersKpi({ database })
      .then((data) => {
        setPeakMau(data.peakMau);
        setPeakMauMonth(data.peakMauMonth ?? '');
        setCurrentEmployerMAU(data.currentEmployerMAU);
        setNewEmployerMAU(data.newEmployerMAU);
        setCurrentBreakdown(data.currentEmployerBreakdown ?? []);
        setNewBreakdown(data.newEmployerBreakdown ?? []);
      })
      .catch(() => {
        setPeakMau(0);
        setPeakMauMonth('');
        setCurrentEmployerMAU(0);
        setNewEmployerMAU(0);
        setCurrentBreakdown([]);
        setNewBreakdown([]);
      })
      .finally(() => {
        setActiveUsersLoading(false);
      });
  }, [database]);

  return (
    <div>
      <h2 style={{ margin: "0 0 24px 0", color: "#1a1a2e", fontSize: 20 }}>
        Q2 2026 KPI Tracker
      </h2>
      <div style={{ display: "flex", gap: 24 }}>
        <KpiBox
          title='Eligible Employees'
          total={18400}
          accentColor='#5B8FF9'
          items={[
            { label: "Sagi", goal: 7800, current: sagiCount, accounts: sagiItems },
            { label: "Ariel", goal: 10600, current: arielCount, accounts: arielItems },
          ]}
          colors={["#5B8FF9", "#E86452"]}
          loading={eligibleLoading}
          notice={
            !eligibleLoading && !mondayConfigured
              ? "Monday not configured"
              : undefined
          }
        />
        <KpiBox
          title='Active Users'
          total={2000}
          accentColor='#5AD8A6'
          items={[
            {
              label: "Existing Companies",
              goal: 1000,
              current: currentEmployerMAU,
              accounts: currentBreakdown,
            },
            {
              label: "New Q2 Companies",
              goal: 1000,
              current: newEmployerMAU,
              accounts: newBreakdown,
            },
          ]}
          colors={["#5AD8A6", "#5B8FF9"]}
          loading={activeUsersLoading}
          notice={
            !activeUsersLoading && peakMau
              ? `all-time peak: ${peakMau.toLocaleString()} (${peakMauMonth})`
              : undefined
          }
        />
      </div>
    </div>
  );
};
